import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { getTopics, getFlatQuestions } from "../../lib/workshop";

const TYPE_LABELS = {
  quick_decision: { label: "Rychlé rozhodnutí", emoji: "⚡" },
  multi_step: { label: "Více kroků", emoji: "🪜" },
  chat_simulation: { label: "Chat simulace", emoji: "💬" },
  feed_analysis: { label: "Analýza feedu", emoji: "🔍" },
  sequence: { label: "Seřazení", emoji: "🧩" },
  vote: { label: "Hlasování", emoji: "🗳️" },
  reflection: { label: "Reflexe", emoji: "📝" },
};

/**
 * Quick read for the lecturer BEFORE starting a session.
 * 2-3 minutes of reading: workshop intro, list of challenges with key methodology hints,
 * resources, time plan. Lecturer goes in confident.
 */
export default function WorkshopPreview() {
  const { workshopId } = useParams();
  const [workshop, setWorkshop] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/workshops/${workshopId}`);
        setWorkshop(data);
      } catch {
        navigate("/teacher/cms");
      }
    })();
  }, [workshopId, navigate]);

  if (!workshop) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Načítám…</div>;
  }

  const topics = getTopics(workshop);
  const flatQuestions = getFlatQuestions(workshop);
  const totalSec = flatQuestions.reduce((acc, c) => acc + (c.duration_seconds || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center font-black">D</div>
            <div>
              <div className="font-heading font-black text-lg tracking-tight text-slate-900">Příprava na hodinu</div>
              <div className="text-xs text-slate-500">2 minuty čtení = klidná session</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="text-sm font-bold text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-200 rounded-md"
              data-testid="btn-print-preview"
            >
              🖨️ Tisk
            </button>
            <Link to="/teacher/cms" className="text-sm font-bold text-slate-500 hover:text-slate-900 px-3 py-1.5">
              ← Zpět na knihovnu
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8 print:py-4">
        {/* Hero */}
        <section className="bg-white border-2 border-slate-900 rounded-lg p-8" data-testid="workshop-hero">
          <div className="text-xs font-black uppercase tracking-widest text-slate-500">Workshop · příprava pro lektora</div>
          <h1 className="text-4xl font-heading font-black tracking-tighter text-slate-900 mt-1">{workshop.title}</h1>
          {workshop.subtitle && (
            <div className="text-lg text-slate-600 mt-1">{workshop.subtitle}</div>
          )}
          <p className="text-base text-slate-700 mt-4">{workshop.description}</p>
          <div className="mt-5 flex flex-wrap gap-2 text-xs">
            {workshop.tags?.map((t) => (
              <span key={t} className="bg-slate-100 text-slate-700 px-2 py-1 rounded-full">#{t}</span>
            ))}
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Délka" value={`${workshop.duration_minutes || 45} min`} />
            <Stat label="Věk" value={workshop.age_range || "—"} />
            <Stat label="Témat" value={topics.length} />
            <Stat label="Otázek" value={flatQuestions.length} />
          </div>
        </section>

        {/* Lector intro */}
        {workshop.lector_intro && (
          <section className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r" data-testid="workshop-intro">
            <div className="text-xs font-black uppercase tracking-widest text-amber-800 mb-2">
              💡 Jak to uvést třídě
            </div>
            <div className="text-slate-900 leading-relaxed">{workshop.lector_intro}</div>
          </section>
        )}

        {/* Time plan grouped by topic */}
        <section>
          <h2 className="text-2xl font-heading font-black tracking-tight text-slate-900 mb-4">
            Časový plán hodiny
          </h2>
          <div className="space-y-4">
            {topics.map((t, ti) => {
              const qs = t.questions || [];
              const topicSec = qs.reduce((a, q) => a + (q.duration_seconds || 0), 0);
              return (
                <div
                  key={t.id}
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                  data-testid={`topic-row-${t.id}`}
                >
                  <div className="bg-slate-900 text-white px-5 py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-widest text-amber-400">
                        Téma {ti + 1} · {qs.length} otázka{qs.length === 1 ? "" : qs.length < 5 ? "y" : ""}
                      </div>
                      <div className="text-lg font-heading font-black tracking-tight truncate">{t.title}</div>
                      {t.intro && <div className="text-xs text-white/70 mt-0.5">{t.intro}</div>}
                    </div>
                    <div className="text-xs tabular-nums text-white/60 flex-shrink-0">~{Math.max(1, Math.round(topicSec / 60))} min</div>
                  </div>
                  {t.lecturer_notes && (t.lecturer_notes.intro || (t.lecturer_notes.discussion || []).length || t.lecturer_notes.takeaway) && (
                    <div className="bg-amber-50 border-b border-amber-200 px-5 py-3 text-sm">
                      {t.lecturer_notes.intro && (
                        <div className="mb-1">
                          <span className="font-black text-[10px] uppercase tracking-widest text-amber-800">Úvod tématu · </span>
                          <span className="text-slate-800">{t.lecturer_notes.intro}</span>
                        </div>
                      )}
                      {Array.isArray(t.lecturer_notes.discussion) && t.lecturer_notes.discussion.length > 0 && (
                        <ul className="list-disc pl-5 text-slate-800 space-y-0.5">
                          {t.lecturer_notes.discussion.map((d, i) => <li key={i}>{d}</li>)}
                        </ul>
                      )}
                      {t.lecturer_notes.takeaway && (
                        <div className="mt-1 text-slate-900 font-medium">→ {t.lecturer_notes.takeaway}</div>
                      )}
                    </div>
                  )}
                  <div className="divide-y divide-slate-100">
                    {qs.map((ch, qi) => {
                      const tl = TYPE_LABELS[ch.type] || { label: ch.type, emoji: "•" };
                      const notes = ch.lecturer_notes;
                      return (
                        <div key={ch.id} className="p-5 hover:bg-slate-50" data-testid={`challenge-row-${ch.id}`}>
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-100 text-slate-900 flex items-center justify-center font-black tabular-nums text-sm">
                              {ti + 1}.{qi + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-black text-slate-900">{ch.title}</span>
                                <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full text-slate-600">
                                  {tl.emoji} {tl.label}
                                </span>
                                <span className="text-xs text-slate-400 tabular-nums">
                                  ~{Math.round((ch.duration_seconds || 60) / 60)} min
                                </span>
                              </div>
                              <p className="text-sm text-slate-600 mt-1.5">{ch.context}</p>

                              {notes && (
                                <div className="mt-3 space-y-2 bg-slate-50 border border-slate-200 rounded-md p-3">
                                  {notes.intro && (
                                    <div>
                                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Úvod do výzvy</div>
                                      <div className="text-sm text-slate-800">{notes.intro}</div>
                                    </div>
                                  )}
                                  {Array.isArray(notes.discussion) && notes.discussion.length > 0 && (
                                    <div>
                                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-1">Otázky k debatě</div>
                                      <ul className="list-disc pl-5 text-sm text-slate-800 space-y-0.5">
                                        {notes.discussion.map((d, idx) => (
                                          <li key={idx}>{d}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {notes.takeaway && (
                                    <div className="bg-amber-50 border-l-2 border-amber-400 px-3 py-2 rounded-r">
                                      <div className="text-[10px] font-black uppercase tracking-widest text-amber-800">Klíčové sdělení</div>
                                      <div className="text-sm text-slate-900 font-medium">{notes.takeaway}</div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md px-5 py-3 text-xs text-slate-500 flex justify-between mt-3">
            <span>Celkem témat: <strong className="text-slate-900">{topics.length}</strong> · otázek: <strong className="text-slate-900">{flatQuestions.length}</strong></span>
            <span>Čistý čas (bez debat): ~<strong className="text-slate-900">{Math.round(totalSec / 60)} min</strong></span>
          </div>
        </section>

        {/* Resources */}
        {Array.isArray(workshop.key_resources) && workshop.key_resources.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-lg p-6" data-testid="workshop-resources">
            <h2 className="text-xl font-heading font-black tracking-tight text-slate-900 mb-3">
              📞 Kontakty pro pomoc (odkaž žáky během debaty)
            </h2>
            <ul className="space-y-2">
              {workshop.key_resources.map((r, i) => (
                <li key={i} className="text-sm text-slate-800 flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5">•</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Quick checklist */}
        <section className="bg-slate-900 text-white rounded-lg p-6" data-testid="workshop-checklist">
          <h2 className="text-xl font-heading font-black tracking-tight mb-3">⚡ Rychlý checklist před hodinou</h2>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2"><span className="text-amber-400">▸</span> Spustit projektor a otestovat odkaz pro žáky</li>
            <li className="flex items-start gap-2"><span className="text-amber-400">▸</span> Mít v záloze Linku bezpečí (116 111) — pro citlivé reakce</li>
            <li className="flex items-start gap-2"><span className="text-amber-400">▸</span> Nastavit Bezpečný režim, pokud je téma citlivé</li>
            <li className="flex items-start gap-2"><span className="text-amber-400">▸</span> Připomenout: anonymita, žádná soutěž, žádná „špatná" odpověď</li>
            <li className="flex items-start gap-2"><span className="text-amber-400">▸</span> Po hodině: 5 min na otevřenou diskusi a osobní svěření</li>
          </ul>
        </section>

        <div className="flex justify-end gap-3 print:hidden">
          <Link
            to="/teacher"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md px-5 py-2.5"
            data-testid="btn-start-session-from-preview"
          >
            🚀 Spustit session
          </Link>
        </div>
      </main>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-xl font-heading font-black tracking-tight text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
